import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { Box, Globe, MoreHorizontal } from "lucide-react";
import DeleteFieldMappingModal from "../../../linked-accounts/detail-page/field-mappings/DeleteFieldMappingModal";
import { navigateToConfigurationFieldMappingTargets } from "../../../../../router/RouterUtils";
import useAppContext from "../../../../context/useAppContext";
import {
  Badge,
  Button,
  ButtonVariant,
  Card,
  Dialog,
  displayNameForAPICategory,
  Menu,
  MenuItem,
  Text,
  TextField,
} from "@merge-api/merge-javascript-shared";
import { APICategory } from "../../../../../models/Entities";
import { showErrorToast, showSuccessToast } from "../../../../shared/Toasts";
import {
  deleteCommonModelOverrideTarget,
  patchFieldMappingTarget,
} from "../utils/FieldMappingUtils";
import { debounce } from "lodash";

type FieldMappingHeaderProps = {
  targetId: string;
  commonModel: string;
  title: string;
  category: APICategory;
  description: string;
  isOverride?: boolean;
};

const FieldMappingHeader = ({
  targetId,
  commonModel,
  title,
  category,
  description,
  isOverride = false,
}: FieldMappingHeaderProps) => {
  const [showDeleteFieldMappingModal, setShowDeleteFieldMappingModal] = useState(false);
  const [showDeleteOverrideModal, setShowDeleteOverrideModal] = useState(false);
  const [isEditingFieldDescription, setIsEditingFieldDescription] = useState(false);

  // local source of truth for what is currently displayed
  const [currentFieldDescription, setCurrentFieldDescription] = useState<string>(description);
  // text the user is typing when in “edit” mode
  const [draftFieldDescription, setDraftFieldDescription] = useState<string>(description);
  const [fieldDescriptionHasChanges, setFieldDescriptionHasChanges] = useState(false);

  const history = useHistory();
  const { user } = useAppContext();

  const debouncedSetFieldDescription = useCallback(
    debounce((value: string) => setDraftFieldDescription(value), 30),
    [],
  );

  // keep the Save button state in sync
  useEffect(() => {
    setFieldDescriptionHasChanges(draftFieldDescription !== currentFieldDescription);
  }, [draftFieldDescription, currentFieldDescription]);

  // if the prop ever changes from above, sync local state
  useEffect(() => {
    setCurrentFieldDescription(description);
    setDraftFieldDescription(description);
  }, [description]);

  return (
    <>
      <DeleteFieldMappingModal
        fieldMappingTargetId={targetId}
        isFieldMappingTargetDelete
        handlePostDelete={() => navigateToConfigurationFieldMappingTargets(history)}
        showModal={showDeleteFieldMappingModal}
        onHide={() => setShowDeleteFieldMappingModal(false)}
        headerText="Delete target field"
        text="This will delete all Field Mappings associated with this target field."
      />
      <Dialog
        open={showDeleteOverrideModal}
        onClose={() => setShowDeleteOverrideModal(false)}
        onPrimaryButtonClick={() =>
          deleteCommonModelOverrideTarget(
            targetId,
            () => {
              showSuccessToast("Successfully deleted override!");
              setShowDeleteOverrideModal(false);
              navigateToConfigurationFieldMappingTargets(history);
            },
            () => showErrorToast("Unable to delete override!"),
          )
        }
        primaryButtonText="Delete override"
        onSecondaryButtonClick={() => setShowDeleteOverrideModal(false)}
        secondaryButtonText="Cancel"
        title="Delete override"
        variant="sm"
      >
        This will delete all Field Mappings associated with this override.
        <br />
        <br />
        Do you wish to continue?
      </Dialog>
      <div>
        <div className={`h-2 rounded-t-lg ${isOverride ? "bg-blue-20" : "bg-orange-20"}`} />

        <Card
          size="lg"
          className="flex flex-row px-6 pt-3 pb-5 transition-all duration-300 ease-in-out overflow-hidden"
        >
          <div className="flex flex-col flex-grow-1 gap-y-2 w-full">
            <Text variant="lg-mono" className="text-xl font-semibold">
              {title}
            </Text>
            <div className="flex flex-row items-center gap-2">
              {isOverride ? (
                <Badge color="blue" className="flex items-center">
                  <Box size={12} className="mr-1" />
                  <span>Common Model override</span>
                </Badge>
              ) : (
                <Badge color="orange" className="flex items-center">
                  <Globe size={12} className="mr-1" />
                  <span className="font-normal">{user.organization.name}-wide target field</span>
                </Badge>
              )}
              <Badge>{commonModel}</Badge>
              <Badge color="blue">{displayNameForAPICategory(category)}</Badge>
            </div>
            <div
              className={`description-container transition-all duration-300 ease-in-out ${
                isEditingFieldDescription ? "max-h-[200px]" : "max-h-[50px]"
              }`}
            >
              {isEditingFieldDescription && !isOverride ? (
                <div className="flex flex-col gap-y-2">
                  <TextField
                    value={draftFieldDescription}
                    onChange={(e) => debouncedSetFieldDescription(e.target.value)}
                    className="w-full"
                  />
                  <div className="flex flex-row items-center justify-end gap-x-3">
                    <Button
                      size="sm"
                      variant={ButtonVariant.TextBlack}
                      onClick={() => setIsEditingFieldDescription(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      variant={ButtonVariant.TertiaryWhite}
                      disabled={!fieldDescriptionHasChanges}
                      onClick={() =>
                        patchFieldMappingTarget({
                          fieldMappingTargetId: targetId,
                          body: { field_description: draftFieldDescription },
                          onResponse: () => {
                            setCurrentFieldDescription(draftFieldDescription);
                            showSuccessToast("Successfully edited field description!");
                            setIsEditingFieldDescription(false);
                          },
                          onError: () => showErrorToast("Failed to update field description"),
                        })
                      }
                    >
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <Text>{currentFieldDescription}</Text>
              )}
            </div>
          </div>
          {!isEditingFieldDescription && (
            <div className="flex flex-col justify-center">
              <Menu
                ButtonProps={{
                  children: <MoreHorizontal size={12} />,
                  size: "sm",
                  variant: ButtonVariant.IconOnly,
                  "aria-haspopup": "true",
                }}
                menuPlacement="bottom-end"
              >
                {!isOverride && (
                  <MenuItem onClick={() => setIsEditingFieldDescription(true)}>
                    Edit field description
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() =>
                    isOverride
                      ? setShowDeleteOverrideModal(true)
                      : setShowDeleteFieldMappingModal(true)
                  }
                >
                  Delete {isOverride ? "override" : "target field"}
                </MenuItem>
              </Menu>
            </div>
          )}
        </Card>
      </div>
    </>
  );
};

export default FieldMappingHeader;
